/**
 * Hero Carousel Web Component - Pure GSAP Version with Infinite Loop
 * Uses cloned slides for seamless infinite scrolling effect
 */
class HeroCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isAnimating = false;
    this.gsapReady = false;
    this.originalSlides = [];
    this.slides = [];
    this.slideCount = 0;
    this.totalSlides = 0;
    this.containerWidth = 0;
    this.autoplayTimer = null;
    this.resizeObserver = null;
    this.clonesPerSide = 2;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplayDelay = parseFloat(this.dataset.autoplayDelay) * 1000;
    this.speed = parseFloat(this.dataset.speed) * 1000;
    this.loop = this.dataset.loop === 'true';
    this.slideCount = parseInt(this.dataset.slideCount) || 0;

    this.fitOverlayText();
    this.watchFontLoading();
    this.waitForDependencies();
  }

  disconnectedCallback() {
    this.stopAutoplay();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  waitForDependencies() {
    const checkDeps = setInterval(() => {
      if (typeof gsap !== 'undefined') {
        clearInterval(checkDeps);
        this.init();
      }
    }, 50);

    setTimeout(() => clearInterval(checkDeps), 5000);
  }

  init() {
    this.initGSAP();
    this.cacheElements();

    if (this.loop && this.slideCount > 1) {
      this.createClones();
    }

    this.calculateDimensions();
    this.fitOverlayText();
    this.setInitialState();
    this.bindEvents();
    this.setupResizeObserver();

    if (this.autoplay && this.slideCount > 1) {
      this.startAutoplay();
    }
  }

  initGSAP() {
    this.gsapReady = true;

    if (typeof CustomEase !== 'undefined') {
      gsap.registerPlugin(CustomEase);
      CustomEase.create('heroEase', '0.22, 0.61, 0.36, 1');
    }
  }

  cacheElements() {
    this.track = this.querySelector('.hero-carousel__track');
    this.originalSlides = Array.from(this.querySelectorAll('.hero-carousel__slide'));
    this.slides = this.originalSlides;
    this.prevBtn = this.querySelector('.hero-carousel__nav-button--prev');
    this.nextBtn = this.querySelector('.hero-carousel__nav-button--next');
    this.thumbnails = Array.from(this.querySelectorAll('.hero-carousel__thumbnail'));
    this.container = this.querySelector('.hero-carousel__container');
  }

  createClones() {
    const slidesToClone = Math.min(this.clonesPerSide, this.slideCount);

    // Clone last slides and prepend them (last slide should be adjacent to first real slide)
    for (let i = 0; i < slidesToClone; i++) {
      const cloneIndex = this.slideCount - 1 - i;
      const clone = this.originalSlides[cloneIndex].cloneNode(true);
      clone.classList.add('is-clone');
      clone.setAttribute('data-clone-of', cloneIndex);
      clone.removeAttribute('data-slide-index');
      this.track.insertBefore(clone, this.track.firstChild);
    }

    // Clone first slides and append them
    for (let i = 0; i < slidesToClone; i++) {
      const clone = this.originalSlides[i].cloneNode(true);
      clone.classList.add('is-clone');
      clone.setAttribute('data-clone-of', i);
      clone.removeAttribute('data-slide-index');
      this.track.appendChild(clone);
    }

    // Update slides array to include clones
    this.slides = Array.from(this.querySelectorAll('.hero-carousel__slide'));
    this.totalSlides = this.slides.length;

    // Adjust currentIndex to account for prepended clones
    this.currentIndex = slidesToClone;
  }

  /**
   * Shrinks the large vertical overlay text only on the slides where the word
   * would run past the slide edges. Other slides keep the CSS font size.
   */
  fitOverlayText() {
    const overlays = Array.from(this.querySelectorAll('.hero-carousel__text-overlay-inner'));
    if (!overlays.length) return;

    const maxHeightSetting = parseFloat(getComputedStyle(this).getPropertyValue('--hero-carousel-text-max-height'));
    const maxRatio = (isNaN(maxHeightSetting) ? 85 : maxHeightSetting) / 100;

    overlays.forEach((inner) => inner.style.setProperty('--text-fit-scale', '1'));

    overlays.forEach((inner) => {
      const slide = inner.closest('.hero-carousel__slide-inner');
      if (!slide) return;

      const available = slide.clientHeight * maxRatio;
      const natural = Math.max(inner.offsetHeight, inner.scrollHeight);
      if (!available || !natural) return;

      const scale = available / natural;
      if (scale < 1) {
        inner.style.setProperty('--text-fit-scale', scale.toFixed(3));
      }
    });
  }

  watchFontLoading() {
    if (!document.fonts || !document.fonts.ready) return;

    document.fonts.ready.then(() => this.fitOverlayText());
  }

  calculateDimensions() {
    const styles = getComputedStyle(this);
    this.containerWidth = this.container.offsetWidth;

    const isMobile = window.innerWidth < 750;
    const isTablet = window.innerWidth >= 750 && window.innerWidth < 990;

    if (isMobile) {
      this.gap = 0;
      this.activeWidth = this.containerWidth;
      this.sideWidth = this.containerWidth * 0.15;
    } else if (isTablet) {
      this.gap = parseInt(styles.getPropertyValue('--hero-carousel-gap')) || 8;
      this.activeWidth = this.containerWidth * 0.7;
      this.sideWidth = this.containerWidth * 0.15;
    } else {
      this.gap = parseInt(styles.getPropertyValue('--hero-carousel-gap')) || 8;
      this.activeWidth = this.containerWidth * (parseInt(styles.getPropertyValue('--hero-carousel-active-width')) || 60) / 100;
      this.sideWidth = this.containerWidth * (parseInt(styles.getPropertyValue('--hero-carousel-side-width')) || 20) / 100;
    }
  }

  setInitialState() {
    this.slides.forEach((slide, index) => {
      const width = index === this.currentIndex ? this.activeWidth : this.sideWidth;
      gsap.set(slide, { width });

      if (index === this.currentIndex) {
        slide.classList.add('is-active');
      } else {
        slide.classList.remove('is-active');
      }
    });

    this.positionTrack(false);
    this.resetAllSlideContent();

    setTimeout(() => {
      this.animateSlideContentIn(this.slides[this.currentIndex]);
    }, 100);
  }

  positionTrack(animate = true) {
    let offset = 0;

    for (let i = 0; i < this.currentIndex; i++) {
      offset += this.sideWidth + this.gap;
    }

    const centerOffset = (this.containerWidth - this.activeWidth) / 2;
    const translateX = centerOffset - offset;

    if (animate && this.gsapReady) {
      gsap.to(this.track, {
        x: translateX,
        duration: this.speed / 1000,
        ease: 'heroEase'
      });
    } else {
      gsap.set(this.track, { x: translateX });
    }
  }

  getRealIndex(index) {
    if (!this.loop || this.slideCount <= 1) return index;

    const slidesToClone = Math.min(this.clonesPerSide, this.slideCount);

    // Convert internal index (with clones) to real slide index
    let realIndex = index - slidesToClone;

    if (realIndex < 0) {
      realIndex = this.slideCount + realIndex;
    } else if (realIndex >= this.slideCount) {
      realIndex = realIndex - this.slideCount;
    }

    return realIndex;
  }

  goToSlide(targetRealIndex, fromThumbnail = false) {
    if (this.isAnimating) return;

    let targetIndex;

    if (fromThumbnail) {
      // When clicking thumbnail, go to the real slide (not clone)
      const slidesToClone = Math.min(this.clonesPerSide, this.slideCount);
      targetIndex = targetRealIndex + slidesToClone;
    } else {
      targetIndex = targetRealIndex;
    }

    if (targetIndex === this.currentIndex) return;

    this.isAnimating = true;
    this.stopAutoplay();

    const previousIndex = this.currentIndex;
    const previousSlide = this.slides[previousIndex];

    // For non-loop mode, clamp the index
    if (!this.loop) {
      targetIndex = Math.max(0, Math.min(targetIndex, this.slideCount - 1));
    }

    this.currentIndex = targetIndex;
    const currentSlide = this.slides[this.currentIndex];

    this.animateSlideContentOut(previousSlide);

    const tl = gsap.timeline({
      onComplete: () => {
        previousSlide.classList.remove('is-active');
        currentSlide.classList.add('is-active');

        // Check if we need to jump to real slide (infinite loop)
        if (this.loop && this.slideCount > 1) {
          this.checkAndRepositionIfNeeded();
        }

        this.isAnimating = false;
        this.animateSlideContentIn(this.slides[this.currentIndex]);
        this.updateThumbnails();

        if (this.autoplay) {
          this.startAutoplay();
        }
      }
    });

    tl.to(previousSlide, {
      width: this.sideWidth,
      duration: this.speed / 1000,
      ease: 'heroEase'
    }, 0);

    tl.to(currentSlide, {
      width: this.activeWidth,
      duration: this.speed / 1000,
      ease: 'heroEase'
    }, 0);

    let offset = 0;
    for (let i = 0; i < this.currentIndex; i++) {
      offset += this.sideWidth + this.gap;
    }

    const centerOffset = (this.containerWidth - this.activeWidth) / 2;
    const translateX = centerOffset - offset;

    tl.to(this.track, {
      x: translateX,
      duration: this.speed / 1000,
      ease: 'heroEase'
    }, 0);
  }

  checkAndRepositionIfNeeded() {
    const slidesToClone = Math.min(this.clonesPerSide, this.slideCount);
    const firstRealIndex = slidesToClone;
    const lastRealIndex = slidesToClone + this.slideCount - 1;

    let needsReposition = false;
    let newIndex = this.currentIndex;

    // If we're on a clone at the end, jump to the real slide at the beginning
    if (this.currentIndex > lastRealIndex) {
      newIndex = firstRealIndex + (this.currentIndex - lastRealIndex - 1);
      needsReposition = true;
    }
    // If we're on a clone at the beginning, jump to the real slide at the end
    else if (this.currentIndex < firstRealIndex) {
      newIndex = lastRealIndex - (firstRealIndex - this.currentIndex - 1);
      needsReposition = true;
    }

    if (needsReposition) {
      // Update the slide states
      this.slides[this.currentIndex].classList.remove('is-active');
      this.currentIndex = newIndex;
      this.slides[this.currentIndex].classList.add('is-active');

      // Reset all slide widths
      this.slides.forEach((slide, index) => {
        const width = index === this.currentIndex ? this.activeWidth : this.sideWidth;
        gsap.set(slide, { width });
      });

      // Instantly reposition (no animation)
      this.positionTrack(false);

      // Reset content animation state for the new current slide
      this.resetSlideContent(this.slides[this.currentIndex]);
    }
  }

  resetSlideContent(slide) {
    const textInner = slide.querySelector('.hero-carousel__text-overlay-inner');
    const buttonEl = slide.querySelector('[data-animate="button"]');
    const overlayEl = slide.querySelector('.hero-carousel__overlay');

    if (textInner) {
      gsap.set(textInner, { clipPath: 'inset(0 0 0% 0)', opacity: 1 });
    }
    if (buttonEl) {
      gsap.set(buttonEl, { opacity: 1, y: 0 });
    }
    if (overlayEl) {
      gsap.set(overlayEl, { x: '0%' });
    }
  }

  goToPrev() {
    if (this.loop || this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  goToNext() {
    const maxIndex = this.loop ? this.totalSlides - 1 : this.slideCount - 1;
    if (this.loop || this.currentIndex < maxIndex) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  resetAllSlideContent() {
    this.slides.forEach(slide => {
      const textInner = slide.querySelector('.hero-carousel__text-overlay-inner');
      const buttonEl = slide.querySelector('[data-animate="button"]');
      const overlayEl = slide.querySelector('.hero-carousel__overlay');

      if (textInner) {
        gsap.set(textInner, { clipPath: 'inset(0 0 100% 0)', opacity: 0 });
      }
      if (buttonEl) {
        gsap.set(buttonEl, { opacity: 0, y: 30 });
      }
      if (overlayEl) {
        gsap.set(overlayEl, { x: '-100%' });
      }
    });
  }

  animateSlideContentIn(slide) {
    if (!slide || !this.gsapReady) return;

    const textEl = slide.querySelector('[data-animate="text"]');
    const buttonEl = slide.querySelector('[data-animate="button"]');
    const overlayEl = slide.querySelector('.hero-carousel__overlay');

    const ease = typeof CustomEase !== 'undefined' ? 'heroEase' : 'power3.out';
    const baseDuration = this.speed / 1000;
    const tl = gsap.timeline({ defaults: { ease } });

    if (overlayEl) {
      tl.fromTo(
        overlayEl,
        { x: '-100%' },
        { x: '0%', duration: baseDuration * 0.9, ease: 'power2.out' },
        0
      );
    }

    if (textEl) {
      const textInner = textEl.querySelector('.hero-carousel__text-overlay-inner');
      if (textInner) {
        tl.fromTo(
          textInner,
          { clipPath: 'inset(0 0 100% 0)', opacity: 0 },
          { clipPath: 'inset(0 0 0% 0)', opacity: 1, duration: baseDuration * 1.1, ease: 'power2.out' },
          baseDuration * 0.25
        );
      }
    }

    if (buttonEl) {
      tl.fromTo(
        buttonEl,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: baseDuration * 0.7, ease: 'power2.out' },
        baseDuration * 0.5
      );
    }
  }

  animateSlideContentOut(slide) {
    if (!slide || !this.gsapReady) return;

    const textEl = slide.querySelector('[data-animate="text"]');
    const buttonEl = slide.querySelector('[data-animate="button"]');
    const overlayEl = slide.querySelector('.hero-carousel__overlay');

    const baseDuration = this.speed / 1000;
    const outDuration = baseDuration * 0.5;
    const tl = gsap.timeline({ defaults: { ease: 'power2.in' } });

    if (buttonEl) {
      tl.to(buttonEl, { opacity: 0, y: -10, duration: outDuration * 0.6 }, 0);
    }

    if (textEl) {
      const textInner = textEl.querySelector('.hero-carousel__text-overlay-inner');
      if (textInner) {
        tl.to(textInner, { clipPath: 'inset(100% 0 0 0)', opacity: 0, duration: outDuration * 0.8 }, 0);
      }
    }

    if (overlayEl) {
      tl.to(overlayEl, { x: '-100%', duration: outDuration }, outDuration * 0.15);
    }
  }

  updateThumbnails() {
    const realIndex = this.getRealIndex(this.currentIndex);
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === realIndex);
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.goToNext();
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  setupResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;

    let resizeTimeout;
    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.calculateDimensions();
        this.fitOverlayText();
        // Re-apply widths and position without recreating clones
        this.slides.forEach((slide, index) => {
          const width = index === this.currentIndex ? this.activeWidth : this.sideWidth;
          gsap.set(slide, { width });
        });
        this.positionTrack(false);
      }, 150);
    });

    this.resizeObserver.observe(this.container);
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goToPrev());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goToNext());
    }

    this.thumbnails.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.slideIndex);
        if (!isNaN(index)) {
          this.goToSlide(index, true);
        }
      });
    });

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (this.autoplay) this.startAutoplay();
    });

    document.addEventListener('keydown', (e) => {
      if (!this.contains(document.activeElement) && document.activeElement !== document.body) return;

      if (e.key === 'ArrowLeft') {
        this.goToPrev();
      } else if (e.key === 'ArrowRight') {
        this.goToNext();
      }
    });

    let touchStartX = 0;
    let touchEndX = 0;

    this.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      this.stopAutoplay();
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          this.goToNext();
        } else {
          this.goToPrev();
        }
      }

      if (this.autoplay) this.startAutoplay();
    }, { passive: true });

    document.addEventListener('shopify:section:load', (e) => {
      if (e.detail.sectionId === this.sectionId) {
        this.disconnectedCallback();
        this.connectedCallback();
      }
    });

    document.addEventListener('shopify:block:select', (e) => {
      if (e.detail.sectionId === this.sectionId) {
        const blockEl = e.target;
        const slideIndex = parseInt(blockEl.dataset.slideIndex);
        if (!isNaN(slideIndex)) {
          this.goToSlide(slideIndex, true);
        }
      }
    });
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
